import { customElement, DeesElement, domtools, type TemplateResult, html, property, type CSSResult, state, cssManager, } from '@design.estate/dees-element';
import { zIndexLayers, zIndexRegistry } from '../../00zindex.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-windowlayer': DeesWindowLayer;
  }
}

export interface IOptions_DeesWindowLayer {
  blur?: boolean;
  /** When false, the layer is a transparent click-catcher with no dim/brightness.
   *  Menus and dropdowns set this false (menus don't dim the backdrop);
   *  modals leave it default (dimmed). */
  dimmed?: boolean;
  /** When true, the layer also dismisses on a capture-phase `pointerdown` anywhere
   *  outside the layer and any `protectedElements` / `protectedSelector`. This is
   *  robust even when app chrome (e.g. the appbar's stacking context) paints above
   *  the layer and would otherwise swallow the overlay's own clicks. Off by default
   *  so dimmed modals/pickers keep relying on a direct backdrop click. */
  dismissOnOutsidePress?: boolean;
}

@customElement('dees-windowlayer')
export class DeesWindowLayer extends DeesElement {
  // STATIC
  public static demo = () => html`<dees-windowlayer></dees-windowlayer>`;
  public static demoGroups = ['Overlay'];

  /* Rendered styles are dynamic (z-index, blur options); the static styles
     only provide the theme tokens the rendered <style> consumes. */
  public static styles = [themeDefaultStyles, cssManager.defaultStyles];

  public static async createAndShow(optionsArg?: IOptions_DeesWindowLayer) {
    const domtoolsInstance = domtools.DomTools.getGlobalDomToolsSync();
    const windowLayer = new DeesWindowLayer();
    windowLayer.options = {
      ...windowLayer.options,
      ...optionsArg,
    }
    document.body.append(windowLayer);
    await domtoolsInstance.convenience.smartdelay.delayFor(0);
    windowLayer.show();
    return windowLayer;
  }

  @state()
  accessor options: IOptions_DeesWindowLayer = {
    blur: false
  };

  @state()
  accessor backdropZIndex: number = 1000;

  @state()
  accessor contentZIndex: number = 1001;

  // INSTANCE
  @property({
    type: Boolean
  })
  accessor visible = false;

  /** Elements whose presses must NOT dismiss the layer (e.g. the menu/dropdown
   *  content and its trigger), used when `dismissOnOutsidePress` is set. */
  public accessor protectedElements: HTMLElement[] = [];

  /** Composed-path selector that, when matched, is treated as "inside" and does
   *  not dismiss — e.g. 'dees-contextmenu' to cover a whole submenu chain. */
  public accessor protectedSelector: string | null = null;

  constructor() {
    super();
    domtools.elementBasic.setup();
  }

  /**
   * Capture-phase outside-press dismissal. Capture means this fires before any
   * ancestor `stopPropagation()` and regardless of z-order, so it works even when
   * app chrome paints above the layer (which blocks the overlay's own clicks).
   * Presses on the layer itself, on a `protectedElements` entry, or matching
   * `protectedSelector` are ignored; anything else re-uses the normal dismissal
   * path (a `click` on the layer) so existing consumer handlers just work.
   */
  private onOutsidePress = (event: PointerEvent) => {
    if (!this.visible) return;
    const path = event.composedPath();
    if (path.includes(this)) return;
    if (this.protectedElements.some((el) => el && path.includes(el))) return;
    if (
      this.protectedSelector &&
      path.some((node) => node instanceof Element && node.matches(this.protectedSelector!))
    ) {
      return;
    }
    this.dispatchEvent(new MouseEvent('click', { bubbles: true, composed: true }));
  };

  async connectedCallback() {
    await super.connectedCallback();
    if (this.options.dismissOnOutsidePress) {
      document.addEventListener('pointerdown', this.onOutsidePress, true);
    }
  }

  async disconnectedCallback() {
    await super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.onOutsidePress, true);
  }

  public render(): TemplateResult {
    return html`
      ${domtools.elementBasic.styles}
      <style>
        .windowOverlay {
          transition:
            background-color var(--dees-transition-default) var(--dees-ease-standard),
            backdrop-filter var(--dees-transition-default) var(--dees-ease-standard);
          will-change: transform;
          position: fixed;
          top: 0px;
          left: 0px;
          height: 100vh;
          width: 100vw;
          display: flex;
          justify-content: center;
          align-items: center;
          background: rgba(0, 0, 0, 0.0);
          -webkit-backdrop-filter: brightness(1) ${this.options.blur ? 'blur(0px)' : ''};
          backdrop-filter: brightness(1) ${this.options.blur ? 'blur(0px)' : ''};
          pointer-events: none;
          z-index: ${this.backdropZIndex};
        }
        .slotContent {
          position: fixed;
          height: 100vh;
          width: 100vw;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: ${this.contentZIndex};
          pointer-events: none;
        }
        
        .slotContent > * {
          pointer-events: auto;
        }
        
        .visible {
          background: ${this.options.dimmed === false ? 'transparent' : 'var(--dees-color-scrim)'};
          -webkit-backdrop-filter: ${this.options.dimmed === false ? 'none' : `brightness(0.9) ${this.options.blur ? 'blur(2px)' : ''}`};
          backdrop-filter: ${this.options.dimmed === false ? 'none' : `brightness(0.9) ${this.options.blur ? 'blur(2px)' : ''}`};
          pointer-events: all;
        }

        @media (prefers-reduced-transparency: reduce) {
          .windowOverlay,
          .visible {
            -webkit-backdrop-filter: none;
            backdrop-filter: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .windowOverlay {
            transition: none;
          }
        }
      </style>
      <div @click=${this.dispatchClicked} class="windowOverlay ${this.visible ? 'visible' : null}">
      </div>
      <div class="slotContent">
        <slot></slot>
      </div>
    `;
  }

  firstUpdated() {
    setTimeout(() => {
      this.visible = true;
    }, 100);
  }

  dispatchClicked() {
    this.dispatchEvent(new CustomEvent('clicked'));
  }

  public toggleVisibility () {
    this.visible = !this.visible;
  }
  
  public getContentZIndex(): number {
    return this.contentZIndex;
  }

  public async show() {
    const domtools = await this.domtoolsPromise;
    
    // Get z-indexes from registry
    this.backdropZIndex = zIndexRegistry.getNextZIndex();
    this.contentZIndex = zIndexRegistry.getNextZIndex();
    
    // Register this element
    zIndexRegistry.register(this, this.backdropZIndex);
    
    await domtools.convenience.smartdelay.delayFor(0);
    this.visible = true;
  }

  public async hide() {
    const domtools = await this.domtoolsPromise;
    await domtools.convenience.smartdelay.delayFor(0);
    this.visible = false;
  }

  public async destroy() {
    const domtools = await this.domtoolsPromise;
    await this.hide();
    await domtools.convenience.smartdelay.delayFor(300);
    
    // Unregister from z-index registry
    zIndexRegistry.unregister(this);
    
    this.remove();
  }
}
